import React, { useEffect, useMemo, useState } from 'react'
import { actionAtom } from 'atoms/atoms'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Box as Wrapper } from '@node-space/storybook-components/dist/Box'
import { Box, EmptyState, RowsSkeletonLoader } from '@node-space/ui-components'
import { Lottie } from 'components/Animations/SimpleLottie'
import SearchingTool from 'components/SearchingTool'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useApiKeysQuery } from 'hooks/queries'
import { ActionIds } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import ApiKeyHeader from '../components/Header/ApiKeyHeader'
import ApiKeysNoData from '../components/Table/ApiKeysNoData'
import { ApiKeyModalProvider } from '../context/ApiKeyModalProvider'
import { ApiKeySheet } from './ApiKeySheet/ApiKeySheet'
import ApiKeysTableV2 from './ApiKeysTable'
import { ApiKeyEnable2FAModal } from './Modal/ApiKeyEnable2FAModal'

const ApiKeys = () => {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [apiKeySheetOpen, setApiKeySheetOpen] = useState(false)
  const [from2FA, setFrom2FA] = useState(false)
  const { data: apiKeyData, isFetching, isError } = useApiKeysQuery(query)

  const setApiKeyAction = useSetAtom(actionAtom)

  const showEmptyState = useMemo(() => {
    return !isError && !isFetching && !apiKeyData?.length && !query
  }, [isError, isFetching, apiKeyData, query])

  const handleOpenApiKeySheetFrom2FAEnable = () => {
    setFrom2FA(true)
    setApiKeySheetOpen(true)
    setApiKeyAction({
      actionId: ActionIds.API_KEY_CREATE,
      header: t('manageApiKeys.generateAPIKeyBtn'),
      onClose: () => {
        setApiKeySheetOpen(false)
        setFrom2FA(false)
      },
    })
  }

  const handleOpenApiKeySheet = () => {
    setApiKeySheetOpen(true)
    setApiKeyAction({
      actionId: ActionIds.API_KEY_CREATE,
      header: t('manageApiKeys.generateAPIKeyBtn'),
      onClose: () => {
        setApiKeySheetOpen(false)
      },
    })
  }

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.MANAGE_API_KEYS, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  return (
    <Wrapper>
      <ApiKeyModalProvider>
        {showEmptyState ? (
          <ApiKeysNoData onSheetOpen={handleOpenApiKeySheet} />
        ) : (
          <Box paddingT={24}>
            <ApiKeyHeader onSheetOpen={handleOpenApiKeySheet} />
            <SearchingTool onSearchChanged={setQuery} query={query} />
          </Box>
        )}

        {isFetching && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

        {isError && (
          <EmptyState
            testId="error-state"
            LottiePlayer={<Lottie src="connection-error" />}
            title={t('defaultError')}
          />
        )}

        <ApiKeySheet isSheetOpen={apiKeySheetOpen} from2FA={from2FA} />
        <ApiKeyEnable2FAModal onComplete={handleOpenApiKeySheetFrom2FAEnable} />

        {!isFetching && !!apiKeyData?.length && (
          <ApiKeysTableV2 apiKeys={apiKeyData} from2FA={from2FA} />
        )}
      </ApiKeyModalProvider>
    </Wrapper>
  )
}

export default React.memo(ApiKeys)
